// Keeps the signed-in token across a page reload.
//
// The token has only ever lived in `window.auth`, so every reload — including
// every one the dev server triggers on a rebuild — went the whole way round
// Azure AD again. Enough of those in quick succession and the tenant starts
// throttling the sign-ins.
//
// sessionStorage rather than localStorage or a cookie: it is scoped to the tab
// and goes when the tab does, which is the same lifetime `EmbeddedPage` already
// gives the token via a session cookie, without attaching it to every request.
// It is no more exposed to script on the page than `window.auth` already is.

const KEY = "borgAuth";
const RETRIED_KEY = "borgAuthRetried";

// Independent of whatever expiry the token carries — this only decides how long
// a reload may reuse one, and the server re-validates it regardless.
const MAX_AGE_MS = 8 * 60 * 60 * 1000;

export function storeAuth(token) {
  if (!token) return;
  try {
    window.sessionStorage.setItem(
      KEY,
      JSON.stringify({ token, at: Date.now() })
    );
    window.sessionStorage.removeItem(RETRIED_KEY);
  } catch (e) {
    // Private browsing or storage disabled; the token stays in memory only and
    // a reload signs in again, which is the behaviour this replaces.
  }
}

// Puts a still-valid token back on `window.auth`, and reports whether it did.
export function restoreAuth() {
  if (window.auth) return true;

  let stored;
  try {
    stored = window.sessionStorage.getItem(KEY);
  } catch (e) {
    return false;
  }
  if (!stored) return false;

  try {
    const { token, at } = JSON.parse(stored);
    if (!token || !at || Date.now() - at > MAX_AGE_MS) {
      clearAuth();
      return false;
    }
    window.auth = token;
    return true;
  } catch (e) {
    clearAuth();
    return false;
  }
}

export function clearAuth() {
  try {
    window.sessionStorage.removeItem(KEY);
  } catch (e) {
    // As above.
  }
}

// The server rejected the token we restored. Drop it and reload, which sends the
// page back through the sign-in — but only once per tab, so a server refusing
// every token cannot put us in a reload loop. Returns whether a reload is coming.
export function retrySignIn() {
  clearAuth();
  window.auth = null;

  try {
    if (window.sessionStorage.getItem(RETRIED_KEY)) return false;
    window.sessionStorage.setItem(RETRIED_KEY, "1");
  } catch (e) {
    return false;
  }

  window.location.reload();
  return true;
}

if (typeof window !== "undefined") {
  window.clearBorgAuth = clearAuth;
}
